import * as React from "react";
import { Box, Card, Chip, Link, Stack, Tooltip, Typography } from "@mui/material";
import { ArrowLeftIcon } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

import { intakeKeysKeys, IntakeRequest, IntakeRequestStatus } from "@/hooks/api/use-intake-keys";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { Breadcrumbs } from "@/components/breadcrumbs/breadcrumbs";
import { RouterLink } from "@/components/core/link";
import { CustomTable } from "@/components/customTable";
import { Column } from "@/components/customTable/types";
import { TableAnimation } from "@/components/table-animation/table-animation";

const STATUS_COLORS: Record<IntakeRequestStatus, "success" | "warning" | "error"> = {
	queued: "success",
	duplicate: "warning",
	rejected: "error",
};

/** Machine codes from the API turned into something an operator can act on. */
const REASON_LABELS: Record<string, string> = {
	campaign_unavailable: "Campaign is disabled or deleted",
	campaign_misconfigured: "Campaign has no agent or contact group",
	no_phone_number: "Campaign has no enabled phone number",
	invalid_phone: "Submitted number was not a valid phone number",
	country_not_allowed: "Number's country is not in the key's allowlist",
	do_not_call: "Contact is tagged do-not-call",
};

export function WebsiteIntakeRequests(): React.JSX.Element {
	const navigate = useNavigate();

	const {
		items: requests,
		isLoading,
		totalItems,
		refetchData: fetchRequests,
	} = useCachedTableStateWithUrl<IntakeRequest>({
		queryKey: intakeKeysKeys.requests(),
		endpoint: "/intake-keys/requests",
	});

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				header: { id: "createdAt", label: "Received", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "createdAt",
				getCellValue: (row: IntakeRequest) => new Date(row.createdAt).toLocaleString(),
			},
			{
				header: { id: "phone", label: "Phone", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "phone",
				getCellValue: (row: IntakeRequest) => (
					<Box component="code" sx={{ fontFamily: "monospace", fontSize: 13 }}>
						{row.phone ?? "-"}
					</Box>
				),
			},
			{
				header: { id: "name", label: "Name", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "name",
				getCellValue: (row: IntakeRequest) => row.name ?? "-",
			},
			{
				header: { id: "source", label: "Source", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "source",
				getCellValue: (row: IntakeRequest) => row.source ?? "-",
			},
			{
				header: { id: "status", label: "Status", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "status",
				getCellValue: (row: IntakeRequest) => (
					<Chip
						label={row.status}
						size="small"
						variant={row.status === "queued" ? "filled" : "outlined"}
						color={STATUS_COLORS[row.status] ?? "default"}
						sx={{ textTransform: "capitalize" }}
					/>
				),
			},
			{
				header: { id: "reason", label: "Reason", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "reason",
				getCellValue: (row: IntakeRequest) => {
					if (!row.reason) return "-";
					return (
						<Tooltip title={row.reason}>
							<Typography variant="body2" sx={{ cursor: "help" }}>
								{REASON_LABELS[row.reason] ?? row.reason}
							</Typography>
						</Tooltip>
					);
				},
			},
			{
				header: { id: "consented", label: "Consent", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "consented",
				getCellValue: (row: IntakeRequest) =>
					row.consented ? (
						<Chip label="Recorded" size="small" variant="outlined" color="success" />
					) : (
						<Tooltip title="The form did not send a consent record. Capture one if you rely on it for compliance.">
							<Chip label="None" size="small" variant="outlined" color="warning" sx={{ cursor: "help" }} />
						</Tooltip>
					),
			},
			{
				header: { id: "contactId", label: "Contact", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "contactId",
				getCellValue: (row: IntakeRequest) =>
					row.contactId ? (
						<Link component="button" variant="body2" onClick={() => navigate(`/contacts/${row.contactId}/view`)}>
							View contact
						</Link>
					) : (
						"-"
					),
			},
		];
	}, [navigate]);

	return (
		<Box
			sx={{
				maxWidth: "var(--Content-maxWidth)",
				m: "var(--Content-margin)",
				p: "var(--Content-padding)",
				width: "var(--Content-width)",
			}}
		>
			<Stack spacing={4}>
				<Stack direction="column" spacing={3}>
					<Breadcrumbs>
						<Link
							color="text.primary"
							component={RouterLink}
							href="/settings/website-intake"
							sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
							variant="subtitle2"
						>
							<ArrowLeftIcon fontSize="var(--icon-fontSize-md)" />
							Website
						</Link>
						<Typography variant="subtitle2" color="text.secondary" whiteSpace="pre">
							/
						</Typography>
						<Typography variant="subtitle2" color="text.secondary" whiteSpace="pre">
							Requests
						</Typography>
					</Breadcrumbs>

					<Box>
						<Typography variant="h4">Callback requests</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
							Every request received from a website form, including the ones that were refused
						</Typography>
					</Box>
				</Stack>

				<TableAnimation delay={0}>
					<Card>
						<CustomTable
							columns={columns}
							data={requests}
							refetchData={fetchRequests}
							isLoading={isLoading}
							totalItems={totalItems}
						/>
					</Card>
				</TableAnimation>
			</Stack>
		</Box>
	);
}
